import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../home/components/Footer';
import LineFloatingButton from '../../components/feature/LineFloatingButton';
import { useSEO } from '../../utils/seo';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useSEO({
    title: 'お問い合わせ | 赤帽みなとサービス - 長崎県の軽運送サービス',
    description: '長崎県の軽運送サービス「赤帽みなとサービス」へのお問い合わせページ。引越し、家具運搬、不用品整理、貨物配送のご相談・お見積もりはこちらから。24時間365日対応。お見積もり無料。',
    keywords: '長崎,軽運送,お問い合わせ,見積もり,引越し,家具運搬,不用品整理,貨物配送,赤帽みなとサービス',
    canonical: '/contact',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'お問い合わせ',
      description: '赤帽みなとサービスへのお問い合わせページ。引越し、家具運搬、不用品整理、貨物配送のご相談を承ります。',
      url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`,
      mainEntity: {
        '@type': 'LocalBusiness',
        name: '赤帽みなとサービス',
        telephone: '0120-743-643',
        address: {
          '@type': 'PostalAddress',
          addressRegion: '長崎県',
          addressCountry: 'JP'
        }
      }
    }
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://readdy.ai/api/form/d63h2eabdoed7v0v8oa0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-24 md:pt-32 pb-10 md:pb-16 bg-[#ede9e3]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <div className="inline-block bg-[#ebe960] rounded-full px-4 md:px-5 py-1.5 md:py-2 mb-4 md:mb-6">
            <p className="text-xs md:text-sm font-bold text-gray-900">CONTACT</p>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 md:mb-6">
            お問い合わせ
          </h1>
          <p className="text-sm md:text-lg lg:text-xl text-gray-700 max-w-2xl mx-auto">
            ご質問・ご相談・お見積もりなど、<br className="sm:hidden" />
            お気軽にお問い合わせください
          </p>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-10 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 mb-10 md:mb-16">
            {/* 電話 */}
            <a
              href="tel:0120-743-643"
              className="bg-[#ebe960] rounded-xl md:rounded-2xl p-5 md:p-8 text-center hover:scale-[1.02] transition-all cursor-pointer"
            >
              <div className="w-12 h-12 md:w-16 md:h-16 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                <i className="ri-phone-fill text-xl md:text-3xl text-[#ebe960]"></i>
              </div>
              <h3 className="text-base md:text-xl font-bold text-gray-900 mb-1 md:mb-2">お電話でのお問い合わせ</h3>
              <p className="text-xl md:text-3xl font-bold text-gray-900 mb-1 md:mb-2">0120-743-643</p>
              <p className="text-xs md:text-sm text-gray-700">24時間365日対応</p>
            </a>

            {/* LINE */}
            <a
              href="https://lin.ee/Rm6h2gK"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#06C755] rounded-xl md:rounded-2xl p-5 md:p-8 text-center hover:scale-[1.02] transition-all cursor-pointer"
            >
              <div className="w-12 h-12 md:w-16 md:h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                <i className="ri-line-fill text-xl md:text-3xl text-[#06C755]"></i>
              </div>
              <h3 className="text-base md:text-xl font-bold text-white mb-1 md:mb-2">LINEでのお問い合わせ</h3>
              <p className="text-sm md:text-lg font-bold text-white mb-1 md:mb-2">写真を送るだけで簡単見積もり</p>
              <p className="text-xs md:text-sm text-white/90">友だち追加してお気軽にどうぞ</p>
            </a>

            {/* メール */}
            <div className="bg-gray-100 rounded-xl md:rounded-2xl p-5 md:p-8 text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                <i className="ri-mail-fill text-xl md:text-3xl text-[#ebe960]"></i>
              </div>
              <h3 className="text-base md:text-xl font-bold text-gray-900 mb-1 md:mb-2">メールでのお問い合わせ</h3>
              <p className="text-sm md:text-lg font-bold text-gray-900 mb-1 md:mb-2">下記フォームより送信</p>
              <p className="text-xs md:text-sm text-gray-600">24時間受付・翌営業日までに返信</p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="max-w-3xl mx-auto">
            <div className="bg-[#ede9e3] rounded-2xl md:rounded-3xl p-5 sm:p-6 md:p-8 lg:p-12">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-6 md:mb-8 text-center">
                メールフォーム
              </h2>

              {submitStatus === 'success' && (
                <div className="mb-6 md:mb-8 bg-green-100 border border-green-400 text-green-700 px-4 md:px-6 py-3 md:py-4 rounded-xl text-center">
                  <i className="ri-check-line text-xl md:text-2xl mb-1 md:mb-2"></i>
                  <p className="font-bold text-sm md:text-base">送信が完了しました</p>
                  <p className="text-xs md:text-sm mt-1">担当者より折り返しご連絡いたします</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 md:mb-8 bg-red-100 border border-red-400 text-red-700 px-4 md:px-6 py-3 md:py-4 rounded-xl text-center">
                  <i className="ri-error-warning-line text-xl md:text-2xl mb-1 md:mb-2"></i>
                  <p className="font-bold text-sm md:text-base">送信に失敗しました</p>
                  <p className="text-xs md:text-sm mt-1">お手数ですが、お電話またはLINEでお問い合わせください</p>
                </div>
              )}

              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-4 md:space-y-6"
              >
                <div>
                  <label htmlFor="name" className="block text-xs md:text-sm font-bold text-gray-900 mb-1.5 md:mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg md:rounded-xl border border-gray-300 focus:border-[#ebe960] focus:ring-2 focus:ring-[#ebe960]/50 outline-none transition-all text-sm"
                    placeholder="山田 太郎"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs md:text-sm font-bold text-gray-900 mb-1.5 md:mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg md:rounded-xl border border-gray-300 focus:border-[#ebe960] focus:ring-2 focus:ring-[#ebe960]/50 outline-none transition-all text-sm"
                    placeholder="example@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-xs md:text-sm font-bold text-gray-900 mb-1.5 md:mb-2">
                    電話番号
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg md:rounded-xl border border-gray-300 focus:border-[#ebe960] focus:ring-2 focus:ring-[#ebe960]/50 outline-none transition-all text-sm"
                    placeholder="090-1234-5678"
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-xs md:text-sm font-bold text-gray-900 mb-1.5 md:mb-2">
                    ご希望のサービス
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg md:rounded-xl border border-gray-300 focus:border-[#ebe960] focus:ring-2 focus:ring-[#ebe960]/50 outline-none transition-all text-sm cursor-pointer"
                  >
                    <option value="">選択してください</option>
                    <option value="引越しサービス">引越しサービス</option>
                    <option value="家具・家電運搬">家具・家電運搬</option>
                    <option value="不用品整理">不用品整理</option>
                    <option value="貨物配送">貨物配送</option>
                    <option value="その他">その他</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs md:text-sm font-bold text-gray-900 mb-1.5 md:mb-2">
                    お問い合わせ内容 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg md:rounded-xl border border-gray-300 focus:border-[#ebe960] focus:ring-2 focus:ring-[#ebe960]/50 outline-none transition-all text-sm resize-none"
                    placeholder="ご質問やご要望をご記入ください（500文字以内）"
                  ></textarea>
                  <p className="text-[10px] md:text-xs text-gray-500 mt-1 text-right">{formData.message.length}/500</p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gray-900 text-white py-3 md:py-4 rounded-full text-base md:text-lg font-bold hover:bg-gray-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      送信中...
                    </span>
                  ) : (
                    '送信する'
                  )}
                </button>
              </form>

              <p className="text-[10px] md:text-xs text-gray-600 mt-4 md:mt-6 text-center leading-relaxed">
                ※ お急ぎの場合は、お電話またはLINEでのお問い合わせをおすすめします。<br />
                ※ 通常、翌営業日までにご返信いたします。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-10 md:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-6 md:mb-8 text-center">
            よくあるご質問
          </h2>
          <div className="space-y-3 md:space-y-4">
            {[
              {
                q: '見積もりは無料ですか？',
                a: 'はい、お見積もりは無料です。お電話、LINE、メールフォームからお気軽にご相談ください。'
              },
              {
                q: '当日の依頼は可能ですか？',
                a: 'はい、可能な限り対応いたします。まずはお電話またはLINEでご連絡ください。'
              },
              {
                q: '対応エリアはどこですか？',
                a: '長崎県内を中心に、全国各地への配送・引越しに対応しています。詳しくはお問い合わせください。'
              },
              {
                q: '支払い方法は何がありますか？',
                a: '現金、銀行振込、各種キャッシュレスに対応しています。法人様は請求書払いも可能です。'
              }
            ].map((item, index) => (
              <div key={index} className="bg-[#ede9e3] rounded-xl md:rounded-2xl p-4 md:p-6">
                <h3 className="text-sm md:text-base font-bold text-gray-900 mb-1.5 md:mb-2 flex items-start gap-2">
                  <span className="text-[#ebe960] font-bold">Q.</span>
                  {item.q}
                </h3>
                <p className="text-xs md:text-sm text-gray-700 leading-relaxed pl-5 md:pl-6">
                  <span className="text-gray-500 font-bold">A.</span> {item.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <LineFloatingButton />
    </div>
  );
}
